import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Building2, Palette, Flower, UtensilsCrossed, Music, Car, Shirt, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

interface StoryPathNavProps {
  categories: string[];
  selected: string;
  onSelect: (category: string) => void;
}

const StoryPathNav: React.FC<StoryPathNavProps> = ({ 
  categories, 
  selected, 
  onSelect 
}) => {
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const [position, setPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragScrollLeft, setDragScrollLeft] = useState(0);
  
  // Get category icon
  const getCategoryIcon = (category: string, isActive: boolean) => {
    const iconProps = { 
      className: `${isActive ? 'text-[#FF4E8E]' : 'text-gray-700 group-hover:text-[#5D5FEF]'}`,
      size: 24,
      strokeWidth: isActive ? 2 : 1.5
    };
    
    switch(category.toLowerCase()) {
      case 'wedding planner':
        return <Calendar {...iconProps} />;
      case 'venues':
        return <Building2 {...iconProps} />;
      case 'photography':
        return <Camera {...iconProps} />;
      case 'decor':
        return <Palette {...iconProps} />;
      case 'florists':
        return <Flower {...iconProps} />;
      case 'catering':
        return <UtensilsCrossed {...iconProps} />;
      case 'entertainment':
      case 'dj':
        return <Music {...iconProps} />;
      case 'transport':
        return <Car {...iconProps} />;
      case 'unique':
        return <Palette {...iconProps} />;
      default:
        return <Palette {...iconProps} />;
    }
  };
  
  // Create duplicated array for infinite effect
  const duplicatedCategories = [...categories, ...categories, ...categories];
  
  // Handle automatic scrolling
  useEffect(() => {
    if (!autoplayEnabled || !containerRef.current || isDragging) return;
    
    let animationId: number;
    let speed = 0.5; // pixels per frame
    
    const animate = () => {
      setPosition(prev => {
        const newPosition = prev - speed;
        
        // Reset position when we've scrolled one full set of categories
        if (Math.abs(newPosition) >= categories.length * 150) { // estimated width per category
          return 0;
        }
        
        return newPosition;
      });
      
      animationId = requestAnimationFrame(animate);
    };
    
    animationId = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [autoplayEnabled, categories.length, isDragging]);
  
  // Handle mouse interaction for dragging
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setAutoplayEnabled(false);
    setStartX(e.pageX - (containerRef.current?.offsetLeft || 0));
    setDragScrollLeft(position);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => setAutoplayEnabled(true), 2000);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    
    const x = e.pageX - (containerRef.current?.offsetLeft || 0);
    const walk = (x - startX) * 2; // Faster scrolling
    setPosition(dragScrollLeft + walk);
  };
  
  // Pause autoplay when hovering over navigation
  const handleMouseEnter = () => {
    setAutoplayEnabled(false);
  };

  // Resume autoplay when mouse leaves
  const handleMouseLeave = () => {
    setIsDragging(false);
    setAutoplayEnabled(true);
  };
  
  // Manual scroll functions
  const handleScrollLeft = () => {
    setAutoplayEnabled(false);
    setPosition(prev => prev + 200);
    setTimeout(() => setAutoplayEnabled(true), 2000);
  };
  
  const handleScrollRight = () => {
    setAutoplayEnabled(false);
    setPosition(prev => prev - 200);
    setTimeout(() => setAutoplayEnabled(true), 2000);
  };

  // Animation variants
  const navContainerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.8,
        staggerChildren: 0.1
      }
    }
  };
  
  const scrollButtonVariants = {
    hover: { 
      scale: 1.1, 
      boxShadow: "0px 8px 20px rgba(93, 95, 239, 0.3)",
      backgroundColor: "rgba(255, 255, 255, 0.95)" 
    },
    tap: { scale: 0.95 }
  };

  return (
    <motion.div 
      className="relative pb-16 pt-12"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      variants={navContainerVariants}
      initial="hidden"
      animate="visible"
      style={{ position: 'relative', zIndex: 40 }}
    >
      <div className="max-w-7xl mx-auto px-4">
        {/* Glassmorphism navigation bar with animated gradient border */}
        <motion.div 
          className="relative backdrop-blur-xl bg-white/60 rounded-3xl shadow-xl overflow-hidden"
          animate={{
            boxShadow: [
              "0 10px 30px rgba(255, 107, 152, 0.1)",
              "0 10px 30px rgba(152, 115, 193, 0.1)",
              "0 10px 30px rgba(65, 105, 225, 0.1)",
              "0 10px 30px rgba(255, 107, 152, 0.1)"
            ]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "loop"
          }}
        >
          {/* Ambient background effects */}
          <motion.div 
            className="absolute inset-0 pointer-events-none"
            style={{ position: 'absolute', zIndex: 1 }}
            animate={{
              background: [
                "linear-gradient(120deg, rgba(255, 105, 180, 0.05) 0%, rgba(65, 105, 225, 0.05) 100%)",
                "linear-gradient(240deg, rgba(255, 107, 152, 0.05) 0%, rgba(152, 115, 193, 0.05) 100%)",
                "linear-gradient(360deg, rgba(255, 105, 180, 0.05) 0%, rgba(65, 105, 225, 0.05) 100%)"
              ]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              repeatType: "loop"
            }}
          />

          {/* Navigation controls - Left */}
          <motion.div 
            className="absolute left-3 top-1/2 -translate-y-1/2"
            style={{ position: 'absolute', zIndex: 43 }}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <motion.button
              onClick={handleScrollLeft}
              className="bg-white/80 backdrop-blur-md border border-[#FF4E8E]/20 rounded-full p-3 text-[#5D5FEF] shadow-lg"
              variants={scrollButtonVariants}
              whileHover="hover"
              whileTap="tap"
              animate={{
                boxShadow: [
                  "0px 4px 12px rgba(255, 78, 142, 0.15)",
                  "0px 4px 12px rgba(93, 95, 239, 0.15)",
                  "0px 4px 12px rgba(255, 78, 142, 0.15)"
                ]
              }}
              transition={{
                boxShadow: {
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "reverse"
                }
              }}
              aria-label="Scroll left"
            >
              <ChevronLeft size={18} />
            </motion.button>
          </motion.div>
          
          {/* Navigation controls - Right */}
          <motion.div 
            className="absolute right-3 top-1/2 -translate-y-1/2"
            style={{ position: 'absolute', zIndex: 43 }}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <motion.button
              onClick={handleScrollRight}
              className="bg-white/80 backdrop-blur-md border border-[#FF4E8E]/20 rounded-full p-3 text-[#5D5FEF] shadow-lg"
              variants={scrollButtonVariants}
              whileHover="hover"
              whileTap="tap"
              animate={{
                boxShadow: [
                  "0px 4px 12px rgba(255, 78, 142, 0.15)",
                  "0px 4px 12px rgba(93, 95, 239, 0.15)",
                  "0px 4px 12px rgba(255, 78, 142, 0.15)"
                ]
              }}
              transition={{
                boxShadow: {
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "reverse"
                }
              }}
              aria-label="Scroll right"
            >
              <ChevronRight size={18} />
            </motion.button>
          </motion.div>

          {/* Category Navigation */}
          <div 
            ref={containerRef}
            className="relative py-8 px-12 bg-gradient-to-r from-[rgba(255,255,255,0.5)] to-[rgba(255,255,255,0.3)] backdrop-blur-xl overflow-hidden"
            style={{ position: 'relative', zIndex: 42 }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            <motion.div 
              className="flex items-center"
              style={{ 
                transform: `translateX(${position}px)`,
                transition: !isDragging ? 'transform 0.3s ease' : 'none'
              }}
            >
              {duplicatedCategories.map((category, idx) => {
                const isActive = category === selected;
                
                return (
                  <div 
                    key={`${category}-${idx}`} 
                    className="flex-shrink-0 px-4"
                  >
                    <motion.button
                      onClick={() => {
                        onSelect(category);
                        // Briefly pause movement when user selects a category
                        setAutoplayEnabled(false);
                        setTimeout(() => setAutoplayEnabled(true), 1500);
                      }}
                      className={`relative group px-6 py-4 rounded-xl transition-all duration-300`}
                      whileHover={{ y: -5 }}
                      whileTap={{ y: 0 }}
                      layout
                    >
                      {/* Background with conditional styling */}
                      <motion.div 
                        className={`absolute inset-0 rounded-xl transition-all duration-300 z-0 ${
                          isActive 
                            ? 'bg-white/80 shadow-lg' 
                            : 'bg-transparent group-hover:bg-white/40'
                        }`}
                        layoutId={`bg-${category}`}
                        transition={{ type: "spring", bounce: 0.2 }}
                      />
                      
                      {/* Glowing effect for active item - Using theme gradient */}
                      {isActive && (
                        <motion.div 
                          className="absolute inset-0 rounded-xl z-0 opacity-70"
                          initial={{ opacity: 0 }}
                          animate={{ 
                            opacity: [0.4, 0.6, 0.4],
                            boxShadow: [
                              "0 0 20px rgba(255, 107, 152, 0.3)",
                              "0 0 30px rgba(152, 115, 193, 0.3)",
                              "0 0 20px rgba(255, 107, 152, 0.3)"
                            ]
                          }}
                          transition={{ 
                            duration: 3,
                            repeat: Infinity,
                            repeatType: "reverse"
                          }}
                        />
                      )}

                      <div className={`
                        relative flex flex-col items-center gap-3 z-10
                      `}>
                        <motion.div
                          animate={isActive ? { 
                            scale: [1, 1.1, 1], 
                            rotate: [0, 5, 0, -5, 0],
                            transition: { 
                              duration: 0.5,
                              repeat: 1,
                              repeatType: "reverse"
                            }
                          } : {}}
                          whileHover={{ scale: 1.1 }}
                          className={`
                            relative h-12 w-12 rounded-full flex items-center justify-center
                            ${isActive 
                              ? 'bg-gradient-to-br from-[#FF69B4]/20 to-[#4169E1]/20 shadow-md border border-[#FF69B4]/30' 
                              : 'bg-white/70 shadow-sm border border-white/50 group-hover:border-[#4169E1]/30'
                            }
                          `}
                        >
                          {/* Icon glow effect - Using primary action color */}
                          {isActive && (
                            <motion.div
                              className="absolute inset-0 rounded-full"
                              animate={{ 
                                boxShadow: [
                                  "0 0 0px rgba(255, 105, 180, 0.5)",
                                  "0 0 10px rgba(255, 105, 180, 0.5)",
                                  "0 0 0px rgba(255, 105, 180, 0.5)"
                                ]
                              }}
                              transition={{ 
                                duration: 2,
                                repeat: Infinity,
                                repeatType: "reverse"
                              }}
                            />
                          )}
                          
                          {getCategoryIcon(category, isActive)}
                        </motion.div>
                        
                        <motion.span 
                          className={`text-base font-medium ${
                            isActive ? 'text-[#5D5FEF]' : 'text-gray-700 group-hover:text-[#5D5FEF]'
                          } transition-colors`}
                        >
                          {category}
                        </motion.span>
                        
                       
                      </div>
                    </motion.button>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default StoryPathNav; 